import { apis } from '@/plugins/apis'

export const reportsConfigStore = {
  namespaced: true,
  state () {
    return {
      companyMeasures: [],
      companyDimensions: [],
      companyFilters: [],
      companyExpressions: [],
      companyMeasureTags: {},
      companyDimensionTags: {},
      companyFilterTags: {},
      companyExpressionTags: {},
      companyMeasureUsages: {},
      companyDimensionUsages: {},
      companyFilterUsages: {},
      companyExpressionUsages: {},
      categoryTags: {},
      configAnalysis: {},
      loading: false,
      loadingOptions: {
        company_measure: false,
        company_dimension: false,
        company_filter: false,
        company_expression: false,
      },
      loadingTags: {
        company_measure: false,
        company_dimension: false,
        company_filter: false,
        company_expression: false,
      },
      loadingUsages: {
        company_measure: false,
        company_dimension: false,
        company_filter: false,
        company_expression: false,
      },
    }
  },
  getters: {
    loading (state) {
      return state.loading
    },
    companyMeasures (state) {
      return state.companyMeasures
    },
    companyDimensions (state) {
      return state.companyDimensions
    },
    companyExpressions (state) {
      return state.companyExpressions
    },
    companyFilters (state) {
      return state.companyFilters
    },
    companyMeasureOptions (state) {
      const options = {}
      for (const companyMeasure of state.companyMeasures) {
        options[companyMeasure.key] = companyMeasure
      }
      return options
    },
    companyDimensionOptions (state) {
      const options = {}
      for (const companyDimension of state.companyDimensions) {
        options[companyDimension.key] = companyDimension
      }
      return options
    },
    companyFilterOptions (state) {
      const options = {}
      for (const companyFilter of state.companyFilters) {
        options[companyFilter.key] = companyFilter
      }
      return options
    },
    companyExpressionOptions (state) {
      const options = {}
      for (const companyExpression of state.companyExpressions) {
        options[companyExpression.key] = companyExpression
      }
      return options
    },
    companyMeasureTags (state) {
      return state.companyMeasureTags
    },
    companyDimensionTags (state) {
      return state.companyDimensionTags
    },
    companyFilterTags (state) {
      return state.companyFilterTags
    },
    companyExpressionTags (state) {
      return state.companyExpressionTags
    },
    categoryTags (state) {
      return state.categoryTags
    },
    companyMeasureUsages (state) {
      return state.companyMeasureUsages
    },
    companyDimensionUsages (state) {
      return state.companyDimensionUsages
    },
    companyFilterUsages (state) {
      return state.companyFilterUsages
    },
    companyExpressionUsages (state) {
      return state.companyExpressionUsages
    },
    loadingOptions (state) {
      return state.loadingOptions
    },
    loadingTags (state) {
      return state.loadingTags
    },
    loadingUsages (state) {
      return state.loadingUsages
    },
  },
  mutations: {
    async setLoading (state, loading) {
      state.loading = loading
    },
    async setCompanyMeasures (state, companyMeasures) {
      state.companyMeasures = companyMeasures
    },
    async setCompanyDimensions (state, companyDimensions) {
      state.companyDimensions = companyDimensions
    },
    async setCompanyExpressions (state, companyExpressions) {
      state.companyExpressions = companyExpressions
    },
    async setCompanyFilters (state, companyFilters) {
      state.companyFilters = companyFilters
    },
    async setAnalysis (state, analysis) {
      state.configAnalysis = analysis
    },
    async setCompanyMeasureTags (state, tags) {
      state.companyMeasureTags = tags
    },
    async setCompanyDimensionTags (state, tags) {
      state.companyDimensionTags = tags
    },
    async setCompanyFilterTags (state, tags) {
      state.companyFilterTags = tags
    },
    async setCompanyExpressionTags (state, tags) {
      state.companyExpressionTags = tags
    },
    async setCategoryTags (state, categoryTags) {
      state.categoryTags = categoryTags
    },
    async setCompanyMeasureUsages (state, usages) {
      state.companyMeasureUsages = usages
    },
    async setCompanyDimensionUsages (state, usages) {
      state.companyDimensionUsages = usages
    },
    async setCompanyFilterUsages (state, usages) {
      state.companyFilterUsages = usages
    },
    async setCompanyExpressionUsages (state, usages) {
      state.companyExpressionUsages = usages
    },
    setLoadingOptions (state, { definitionType, loading, }) {
      state.loadingOptions[definitionType] = loading
    },
    setLoadingTags (state, { definitionType, loading, }) {
      state.loadingTags[definitionType] = loading
    },
    setLoadingUsages (state, { definitionType, loading, }) {
      state.loadingUsages[definitionType] = loading
    },
  },
  actions: {
    async refreshOptions (context) {
      context.commit('setLoading', true)

      const definitionTypes = ['company_measure', 'company_dimension', 'company_filter', 'company_expression',]

      // Set all loading states to true
      for (const type of definitionTypes) {
        context.commit('setLoadingOptions', { definitionType: type, loading: true, })
        context.commit('setLoadingTags', { definitionType: type, loading: true, })
        context.commit('setLoadingUsages', { definitionType: type, loading: true, })
      }

      // Options loading
      const optionsMap = {
        company_measure: { api: apis.companyConfigs.companyMeasure.list, mutation: 'setCompanyMeasures', },
        company_dimension: { api: apis.companyConfigs.companyDimension.list, mutation: 'setCompanyDimensions', },
        company_filter: { api: apis.companyConfigs.companyFilter.list, mutation: 'setCompanyFilters', },
        company_expression: { api: apis.companyConfigs.companyExpression.list, mutation: 'setCompanyExpressions', },
      }

      const tagsMap = {
        company_measure: 'setCompanyMeasureTags',
        company_dimension: 'setCompanyDimensionTags',
        company_filter: 'setCompanyFilterTags',
        company_expression: 'setCompanyExpressionTags',
      }

      const usagesMap = {
        company_measure: 'setCompanyMeasureUsages',
        company_dimension: 'setCompanyDimensionUsages',
        company_filter: 'setCompanyFilterUsages',
        company_expression: 'setCompanyExpressionUsages',
      }

      const tag_categories = {
        company_measure: [
          'measure_category',
          'measure_topic',
          'measure_type',
          'measure_custom',
        ],
        company_dimension: [
          'dimension_category',
          'dimension_custom',
        ],
        company_filter: [
          'filter_category',
          'filter_custom',
        ],
        company_expression: [
        ],
      }

      // Fetch options and update loading state when complete
      const optionsPromises = definitionTypes.map(async (type) => {
        try {
          const data = await optionsMap[type].api()
          context.commit(optionsMap[type].mutation, data)
        } finally {
          context.commit('setLoadingOptions', { definitionType: type, loading: false, })
        }
      })

      const newCategoryTags = {}

      // Fetch tags and update loading state when complete
      const tagsPromises = definitionTypes.map(async (type) => {
        try {
          const dataFuture = apis.companyConfigs.getBulkDefinitionTags(type)
          // Fetch tags by categories to get their display names
          const categoryTagFutures = tag_categories[type].map(
            async (category) => {
              const data = await apis.companyConfigs.getCategoryTags(category)
              newCategoryTags[category] = {}
              if (Array.isArray(data)) {
                data.forEach(tag => {
                  newCategoryTags[category][tag.id] = tag
                })
              }
            }
          )
          const data = await dataFuture
          context.commit(tagsMap[type], data || {})
          await Promise.all(categoryTagFutures)
        } finally {
          context.commit('setLoadingTags', { definitionType: type, loading: false, })
        }
      })

      // Fetch usages and update loading state when complete
      const usagesPromises = definitionTypes.map(async (type) => {
        try {
          const data = await apis.companyConfigs.getBulkDefinitionUsages(type)
          context.commit(usagesMap[type], data || {})
        } finally {
          context.commit('setLoadingUsages', { definitionType: type, loading: false, })
        }
      })

      // Wait for all to complete
      await Promise.all([...optionsPromises, ...tagsPromises, ...usagesPromises,])

      context.commit('setCategoryTags', newCategoryTags)

      context.commit('setLoading', false)
    },
  },
}
